import * as React from "react";
import { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { User, Group } from "./users-types";
import { useTheme } from "@mui/material/styles";
import api from "../../pages/api/base-url";
import { useSWRConfig } from "swr";
import UsersConfirmation from "./AddConfirmationDialog";

const CustomPaper = (props: any) => {
  return <Paper elevation={8} {...props} />;
};

const CustomPopper = (props: any) => {
  const modifiers = [
    {
      name: "flip",
      options: {
        fallbackPlacements: [],
      },
    },
  ];

  return (
    <Popper
      {...props}
      modifiers={modifiers}
      popperOptions={{
        placement: "bottom",
      }}
    />
  );
};

/* SUPERUSER: Component to create and update a group -  Switching between create and update is performed through a boolean value "update" "*/

export default function AddGroupDialog(props: {
  users: User[];
  selectedGroup?: Group;
  update?: boolean;
  open: boolean;
  setOpen: any;
}) {
  const { t } = useTranslation();
  const { mutate } = useSWRConfig();

  const [name, setName] = useState<string>(
    props.update && props.selectedGroup !== undefined
      ? props.selectedGroup.name
      : ""
  );
  const [users, setUsers] = useState<User[]>(
    props.update && props.selectedGroup !== undefined
      ? props.selectedGroup.users.filter((user) => user.status !== "ADMIN")
      : []
  );

  const [uploadGroupInfoLoading, setUploadGroupInfoLoading] = useState(false);

  const [isOpenConfirmation, setIsOpenConfirmation] = useState(false);

  useEffect(() => {
    if (props.update && props.selectedGroup) {
      setName(props.selectedGroup.name);
      setUsers(
        props.selectedGroup.users.filter((user) => user.status !== "ADMIN")
      );
    }
  }, [props.selectedGroup, props.update]);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const isConfirmButtonEnabled = name !== "" && users.length > 0;

  function resetAll() {
    setName("");
    setUsers([]);
  }

  const handleCloseDialog = () => {
    resetAll();
    props.setOpen(false);
  };

  const handleSelectUsers = (event: React.SyntheticEvent, newValue: User[]) => {
    setUsers(newValue);
  };

  function handleChangeGroupName(
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    setName(event.currentTarget.value as string);
  }

  useEffect(() => {
    const findGroupsUser = async () => {
      try {
        if (props.update && props.selectedGroup !== undefined) {
          const response = await api.get(
            `groups/${props.selectedGroup.id}/users`
          );
          setUsers(response.data); //QUI
        }
      } catch (err: any) {
        if (err.response) {
          //Not in 200 response range
          console.log(err.response.data);
          console.log(err.response.status);
        } else {
          console.log(`Error: ${err.message}`);
        }
      }
    };
    findGroupsUser();
  }, [props.selectedGroup, props.update]);

  async function createGroup(name: string, users: User[]) {
    setUploadGroupInfoLoading(true);
    let payload = { name: name, users: users.map((user) => user.id) };
    let res = await api.post("/groups", payload);

    if (res.status >= 200 && res.status < 300) {
      setUploadGroupInfoLoading(false);
      mutate(`/groups`);
      setIsOpenConfirmation(true);
    }
  }

  async function updateGroup(id: number, users: User[]) {
    setUploadGroupInfoLoading(true);
    let payload = users.map((user) => user.id);

    let res = await api.put(`/groups/${id}/users`, payload);

    if (res.status >= 200 && res.status < 300) {
      setUploadGroupInfoLoading(false);
      mutate(`/groups`);
      setIsOpenConfirmation(true);
    }
  }

  function handleSubmit() {
    if (users && name) {
      if (!props.update) {
        createGroup(name, users);
      } else {
        if (props.selectedGroup && props.selectedGroup.id) {
          updateGroup(props.selectedGroup.id, users);
        }
      }
    }
    handleCloseDialog();
  }

  return (
    <Box>
      <Dialog
        open={props.open}
        onClose={handleCloseDialog}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {!props.update ? t("Create a group") : t("Update group")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCloseDialog}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("Choose one name for your group.")}
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} sx={{ paddingY: 3 }}>
              <Grid item xs={12}>
                <TextField
                  label={t("Name")}
                  placeholder={t("Enter group name...")}
                  type="text"
                  fullWidth
                  onChange={handleChangeGroupName}
                  variant="outlined"
                  value={name}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("Add users to your group.")}
                </Typography>
              </Divider>
            </Grid>
            <Grid item xs={12} sx={{ pb: 3 }}>
              <Autocomplete
                multiple
                limitTags={3}
                id="fixed-tags-demo"
                filterSelectedOptions
                PaperComponent={CustomPaper}
                PopperComponent={CustomPopper}
                value={users}
                onChange={handleSelectUsers}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                options={props.users
                  .filter(
                    (user) =>
                      user.status !== "INACTIVE" && user.status !== "ADMIN"
                  )
                  .sort((a, b) => (a.name > b.name ? 1 : -1))}
                getOptionLabel={(option) => `${option.id}`}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip label={option.name} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("Users")}
                    placeholder={t("Select users from the list...")}
                    helperText={
                      t("Please note: ") +
                      t(
                        "Administrators are automatically added to every group."
                      )
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button onClick={handleSubmit} disabled={!isConfirmButtonEnabled}>
            {t("Submit")}
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        display="flex"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        <Dialog
          open={uploadGroupInfoLoading}
          fullScreen={fullScreen}
          fullWidth
          maxWidth="md"
        >
          <DialogContent>
            <Stack alignItems="center" justifyContent="center" spacing={2}>
              <CircularProgress thickness={3} size={80} />
              <Typography textAlign="center" variant="h5">
                {props.update ? t("Updating group...") : t("Creating group...")}
              </Typography>
            </Stack>
          </DialogContent>
        </Dialog>
      </Box>

      <UsersConfirmation
        title={
          props.update
            ? t("Group update successfully!")
            : t("Group created successfully!")
        }
        open={isOpenConfirmation}
        setOpen={setIsOpenConfirmation}
      />
    </Box>
  );
}
